import { useCallback, useEffect, useState } from 'react';

type Props<T extends HTMLElement> = {
  containerRef: React.RefObject<T>;
  initialPos?: { x: number; y: number };
  getDragging?: (dragging: boolean) => void;
};

const useDragDrop = <T extends HTMLElement>({
  containerRef,
  initialPos,
  getDragging,
}: Props<T>) => {
  const [dragging, setDragging] = useState(false);
  const [pos, setPos] = useState(initialPos || { x: 0, y: 0 });

  const handleMouseDown = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    setDragging(true);
    getDragging && getDragging(true);
    setPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging || !containerRef.current) return;

      const changeInX = e.clientX - pos.x;
      const changeInY = e.clientY - pos.y;

      const newLeft = containerRef.current.offsetLeft + changeInX;
      const newTop = containerRef.current.offsetTop + changeInY;

      const rect = containerRef.current.getBoundingClientRect();
      const documentScrollbarWidth = 15;

      if (
        newLeft < 0 ||
        newTop < 0 ||
        newLeft + rect.width > window.innerWidth - documentScrollbarWidth ||
        newTop + rect.height > document.body.clientHeight
      ) {
        return;
      }

      containerRef.current.style.left = `${newLeft}px`;
      containerRef.current.style.top = `${newTop}px`;

      setPos({ x: e.clientX, y: e.clientY });
    },
    [containerRef, dragging, pos.x, pos.y],
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);
    getDragging && getDragging(false);
  }, [getDragging]);

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  return {
    handleMouseDown,
    dragging,
  };
};

export default useDragDrop;
